import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import * as NewUserFlowActions from 'onboarding/new-user-flow/NewUserFlowActions';
import {SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN} from 'onboarding/new-user-flow/NewUserFlowConstants';
import {makeWelcomeText} from 'onboarding/new-user-flow/signup-flow-v2/InfoSidePanel';
import {RAndDIndustry} from 'onboarding/new-user-flow/signup-flow-v2/SignupFlowConstants';
import SignupScreen from 'onboarding/new-user-flow/signup-flow-v2/SignupScreen';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import AsyncValidatedPhoneNumberInput from './AsyncValidatedPhoneNumberInput';

function NextButton({disabled, onSubmit}: {disabled: boolean; onSubmit: () => void}): JSX.Element {
  return (
    <Button
      className="pull-right u-large-spacing-top"
      buttonStyle={ButtonStyle.SECONDARY}
      size={ButtonSize.MEDIUM}
      text="Next"
      onClick={onSubmit}
      disabled={disabled}
    />
  );
}

function IndustryRoleInfoDescription(): JSX.Element {
  return (
    <>
      <Label>{makeWelcomeText()}</Label>
      <div className={css(styles.sidePanelTitleText)}>
        You’ve signed up for <span className={css(styles.highlightedText)}>limited access</span> to
        Benchling’s most popular applications
      </div>
      <div className={classNames(css(styles.sidePanelBodyText), 'u-faded')}>
        This free and limited version of Benchling is designed for academics, but our full platform is
        designed for R&D organizations of all sizes.
      </div>
    </>
  );
}

interface IndustryDropdownProps {
  onChange: (newValue: RAndDIndustry) => void;
  placeholder: string;
  values: Array<RAndDIndustry>;
  selectedValue: string | null;
}

function IndustryDropdown(props: IndustryDropdownProps): JSX.Element {
  const optionDatas: Array<OptionData> = props.values.map((value: RAndDIndustry) => ({
    name: value,
    value,
  }));
  const options = optionDatas.map(({name, value}) => (
    <Option
      key={value}
      onSelect={() => {
        props.onChange(value);
      }}
      value={value}
    >
      {name}
    </Option>
  ));

  return (
    <SelectDropdown
      buttonClassName={classNames(props.selectedValue == null && 'u-faded', css(styles.dropdown))}
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
          {options}
        </Menu>
      }
      displayName={props.selectedValue ?? props.placeholder}
      alwaysDropDown={true}
    />
  );
}

const handleSubmitRoleInfo = (
  company: string,
  title: string,
  phoneNumber: string,
  industry: string | null
): void => {
  analytics.track('onboarding_page', 'clicked', {
    page: 'industry_role_info',
    objectClicked: 'button',
    objectText: 'Next',
    eventLastModified: 'FY22_Q2b3',
    company,
    title,
    phoneNumber,
    industry,
  });
  NewUserFlowActions.submitIndustryRoleInfo(
    {
      company,
      title,
      phoneNumber,
      industry,
    },
    () => {
      NewUserFlowActions.showScreen(SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN);
    }
  );
};

interface IndustryRoleInfoTextInputProps {
  placeholder: string;
  value: string;
  onChange: (input: string) => void;
}

function IndustryRoleInfoTextInput(props: IndustryRoleInfoTextInputProps): JSX.Element {
  return (
    <TextInput
      className={css(styles.textInput)}
      placeholder={props.placeholder}
      value={props.value}
      onChange={(e: InputChangeEvent) => {
        props.onChange(e.target.value);
      }}
    />
  );
}

function IndustryRoleInfoEditor(): JSX.Element {
  const [company, setCompany] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [industry, setIndustry] = React.useState<RAndDIndustry | null>(null);
  const [error, setError] = React.useState('');

  return (
    <>
      <div className={classNames(css(styles.mediumText))}>Build out your profile to give it a try.</div>
      <IndustryRoleInfoTextInput placeholder="Company (required)" value={company} onChange={setCompany} />
      <IndustryRoleInfoTextInput placeholder="Title" value={title} onChange={setTitle} />
      <AsyncValidatedPhoneNumberInput
        className={css(styles.textInput)}
        onValidInput={setPhoneNumber}
        onUpdateErrorMessage={setError}
      />
      <IndustryDropdown
        onChange={(newValue: RAndDIndustry) => {
          setIndustry(newValue);
        }}
        placeholder="R&D industry"
        values={Object.values(RAndDIndustry)}
        selectedValue={industry}
      />
      <ErrorMessage className={css(styles.errorMessage)} error={error} />
      <NextButton
        disabled={company.length === 0 || error.length > 0}
        onSubmit={() => {
          handleSubmitRoleInfo(company, title, phoneNumber, industry);
        }}
      />
    </>
  );
}

export default function IndustryRoleInfoScreen(): JSX.Element {
  return (
    <SignupScreen>
      <Row className={css(styles.row)}>
        <Col xs={6} className={css(styles.industryRoleInfoDescription)}>
          <IndustryRoleInfoDescription />
        </Col>
        <Col xs={6} className={css(styles.industryRoleInfoEditor)}>
          <IndustryRoleInfoEditor />
        </Col>
      </Row>
    </SignupScreen>
  );
}

const styles = StyleSheet.create({
  row: {
    marginLeft: '52px',
    marginRight: '52px',
  },
  industryRoleInfoDescription: {
    marginTop: '76px',
    paddingRight: '0px',
  },
  industryRoleInfoEditor: {
    marginTop: '117px',
    paddingLeft: '22px',
  },
  sidePanelTitleText: {
    fontSize: '26px',
    lineHeight: '30px',
    marginTop: '10px',
  },
  sidePanelBodyText: {
    fontSize: '18px',
    lineHeight: '24px',
    marginTop: '12px',
  },
  highlightedText: {
    color: BLUE_LIGHT,
  },
  mediumText: {
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '11px',
  },
  textInput: {
    minHeight: '40px',
    fontSize: '13px',
    lineHeight: '16px',
    borderRadius: '4px',
    marginBottom: '8px',
  },
  dropdown: {
    minHeight: '40px',
  },
  errorMessage: {
    minHeight: '24px',
    fontSize: '12px',
  },
});
